<!--
  `dlg-self-layout` opts this dialog out of the body gutter that
  `src/dialogs.css` puts on every direct child of a dialog root. It does
  NOT opt out of the shared look: the header band and footer bar below
  read the same `--dlg-*` tokens as every other dialog. The opt-out
  exists because this body is a full-height flex column with a sidebar
  that has to reach the card edge, which a 24px gutter would break.
-->
<div class="split-container dlg-self-layout">

  <!-- ═══ HEADER ═══ -->
  <div class="sp-header">
    <h2 class="sp-title">
      <mat-icon class="sp-title-icon">call_split</mat-icon>
      <span i18n="@@Split Order">Split Order</span>
    </h2>
    <div class="sp-header-actions">
      <button mat-stroked-button (click)="addNewOrder()" class="sp-add-order-btn">
        <mat-icon>add</mat-icon> <span i18n="@@Add New Order">Add New Order</span>
      </button>
    </div>
  </div>

  <!-- ═══ QUANTITY WARNING ═══ -->
  <div *ngIf="hasQuantityMismatch" class="sp-warning-bar">
    <mat-icon>warning</mat-icon>
    <span i18n="@@Item quantities do not match">Item quantities do not match order assignments. All items must be fully assigned.</span>
  </div>

  <!-- ═══ STAGE 1: EDIT ═══ -->
  <ng-container *ngIf="stage === 'edit'">
    <div class="sp-layout">

      <!-- ── ITEMS PANEL ── -->
      <div class="sp-items-panel" [class.sp-items-collapsed]="!itemsPanelExpanded && mobileQueryMatches">
        <h3 class="sp-section-title sp-items-toggle" (click)="itemsPanelExpanded = !itemsPanelExpanded">
          <span i18n="@@Items">Items</span>
          <span class="sp-item-count-badge">{{itemRows.length}}</span>
          <mat-icon class="sp-toggle-icon">{{itemsPanelExpanded || !mobileQueryMatches ? 'expand_less' : 'expand_more'}}</mat-icon>
        </h3>
        <div class="sp-items-list" [class.sp-items-hidden]="!itemsPanelExpanded && mobileQueryMatches">
        <div *ngFor="let item of itemRows; let i = index" class="sp-item-card"
             [class.sp-item-mismatch]="getUnassigned(i) !== 0">
          <div class="sp-item-header">
            <span class="sp-item-title">{{item.title}}</span>
            <span class="sp-item-id">#{{item.bookId}}</span>
          </div>
          <div class="sp-item-details">
            <span class="sp-item-qty">
              <mat-icon class="sp-inline-icon">layers</mat-icon>
              {{item.totalQuantity}} <span i18n="@@total">total</span>
            </span>
            <span class="sp-item-trim">{{item.trimWidth | measureLengthValue:1}}×{{item.trimHeight | measureLength:1}}</span>
          </div>
          <div class="sp-item-details">
            <span class="sp-item-weight">
              <mat-icon class="sp-inline-icon">scale</mat-icon>
              {{item.weightEach | measureWeight:2}} <span i18n="each|Abbreviation for &quot;each&quot;, shown after a per-copy weight, e.g. &quot;0.55 lbs ea&quot;. Not the pronoun.@@ea">ea</span>
            </span>
            <span *ngIf="item.type !== 'INITIAL'" class="sp-item-type">{{item.type}}</span>
          </div>
          <div *ngIf="getUnassigned(i) !== 0" class="sp-item-unassigned"
               [class.sp-item-over]="getUnassigned(i) < 0"
               [class.sp-item-under]="getUnassigned(i) > 0">
            <mat-icon>{{getUnassigned(i) > 0 ? 'remove_circle_outline' : 'error'}}</mat-icon>
            <span *ngIf="getUnassigned(i) > 0">{{getUnassigned(i)}} <span i18n="copies not yet placed in any package|Follows a count, e.g. &quot;3 unassigned&quot;.@@unassigned">unassigned</span></span>
            <span *ngIf="getUnassigned(i) < 0">{{-getUnassigned(i)}} <span i18n="more copies placed in packages than the order contains|Follows a count, e.g. &quot;2 over-assigned&quot;.@@over-assigned">over-assigned</span></span>
          </div>
        </div>
        </div>
      </div>

      <!-- ── ORDERS AREA ── -->
      <div class="sp-orders-panel">
        <div class="sp-orders-scroll">
          <div *ngFor="let col of orderColumns; let j = index" class="sp-order-card"
               [class.sp-order-new]="col.isNew"
               [class.sp-order-empty]="getOrderItemCount(j) === 0"
               [class.sp-order-shipped]="!col.isNew && col.printNodeOrder.shippingStatus === 'SHIPPED'">

            <!-- Order Header -->
            <div class="sp-order-header">
              <div class="sp-order-title-row">
                <span class="sp-order-id">
                  <mat-icon *ngIf="col.isNew" class="sp-new-badge">fiber_new</mat-icon>
                  <span>{{col.label}}</span>
                </span>
                <span *ngIf="!col.isNew && col.printNodeOrder.shippingStatus === 'SHIPPED'"
                      class="sp-order-status sp-status-shipped" i18n="@@SHIPPED">SHIPPED</span>
                <span *ngIf="!col.isNew && col.printNodeOrder.shippingStatus !== 'SHIPPED'"
                      class="sp-order-status sp-status-active" i18n="@@ACTIVE">ACTIVE</span>
              </div>
            </div>

            <!-- Order Summary -->
            <div class="sp-order-summary">
              <div class="sp-order-stat">
                <mat-icon class="sp-inline-icon">layers</mat-icon>
                <span>{{getOrderItemCount(j)}} <span i18n="@@copies">copies</span></span>
              </div>
              <div class="sp-order-stat">
                <mat-icon class="sp-inline-icon">menu_book</mat-icon>
                <span>{{getOrderTitles(j)}} <span i18n="@@titles">titles</span></span>
              </div>
              <div class="sp-order-stat">
                <mat-icon class="sp-inline-icon">scale</mat-icon>
                <span>{{getOrderWeight(j) | measureWeight:1}}</span>
              </div>
            </div>

            <!-- Items in this Order -->
            <div class="sp-order-items">
              <div *ngFor="let item of itemRows; let i = index" class="sp-order-item-row"
                   [class.sp-order-item-active]="quantities[i][j] > 0"
                   [class.sp-order-item-locked]="isCellLocked(i, j) || committedQuantities[i][j] > 0">
                <div class="sp-order-item-info">
                  <span class="sp-order-item-title">{{item.title}}</span>
                  <!-- Lock badges shown when this cell is constrained
                       by a CLOSED ManufacturingSet or by a
                       printed/SHIPPED package commitment. -->
                  <mat-icon *ngIf="isCellLocked(i, j)"
                            class="sp-lock-icon sp-lock-closed-set"
                            [matTooltip]="getCellLockReason(i, j)">lock</mat-icon>
                  <mat-icon *ngIf="!isCellLocked(i, j) && committedQuantities[i][j] > 0"
                            class="sp-lock-icon sp-lock-committed"
                            [matTooltip]="getCellLockReason(i, j)">inventory_2</mat-icon>
                </div>
                <div class="sp-order-item-controls">
                  <button mat-icon-button class="sp-qty-btn" (click)="changeQuantity(i, j, -1)"
                          [disabled]="!canDecreaseQuantity(i, j)"
                          [matTooltip]="isCellLocked(i, j) || committedQuantities[i][j] > 0 ? getCellLockReason(i, j) : 'Remove 1'"
                          i18n-matTooltip="@@Remove 1">
                    <mat-icon>remove</mat-icon>
                  </button>
                  <input type="number" class="sp-order-item-qty-input"
                         [class.sp-qty-zero]="quantities[i][j] === 0"
                         [class.sp-qty-over]="getUnassigned(i) < 0 && quantities[i][j] > 0"
                         [value]="quantities[i][j]"
                         [min]="getMinQuantity(i, j)"
                         [max]="getMaxQuantity(i, j) === 9007199254740991 ? null : getMaxQuantity(i, j)"
                         [disabled]="isCellLocked(i, j)"
                         [title]="getCellLockReason(i, j)"
                         (change)="setQuantity(i, j, $any($event.target).value)">
                  <button mat-icon-button class="sp-qty-btn" (click)="changeQuantity(i, j, 1)"
                          [disabled]="!canIncreaseQuantity(i, j)"
                          [matTooltip]="isCellLocked(i, j) ? getCellLockReason(i, j) : 'Add 1'"
                          i18n-matTooltip="@@Add 1">
                    <mat-icon>add</mat-icon>
                  </button>

                  <!-- Transfer 1 to each other order -->
                  <ng-container *ngFor="let t of getTransferTargets(i, j)">
                    <button mat-icon-button class="sp-transfer-btn" (click)="transferOne(i, j, t)"
                            [disabled]="!canDecreaseQuantity(i, j) || !canIncreaseQuantity(i, t)"
                            [matTooltip]="(isCellLocked(i, j) ? getCellLockReason(i, j) : ('Send 1 → ' + orderColumns[t].label))">
                      <span class="sp-transfer-label">{{orderColumns[t].label}}</span>
                    </button>
                  </ng-container>

                  <!-- Move all here (into this order) -->
                  <button mat-icon-button class="sp-move-all-btn" (click)="moveAllToOrder(i, j)"
                          [disabled]="isMoveAllDisabled(i, j)"
                          matTooltip="Move all into this order" i18n-matTooltip="@@Move all into this order">
                    <mat-icon>keyboard_double_arrow_left</mat-icon>
                  </button>
                </div>
                <div class="sp-order-item-weight" *ngIf="quantities[i][j] > 0">
                  {{quantities[i][j] * item.weightEach | measureWeight:2}} ·
                  {{quantities[i][j] * item.spine | measureLength:1}}
                </div>
              </div>
            </div>

            <!-- Order Actions -->
            <div class="sp-order-actions">
              <button mat-stroked-button color="warn" (click)="removeOrder(j)"
                      [disabled]="!canRemoveOrder(j)" class="sp-remove-btn">
                <mat-icon>delete</mat-icon> <span i18n="take out of a list|Takes an item out of the current list or set. The item itself continues to exist elsewhere. Distinct from Delete, which destroys it.@@Remove">Remove</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── PENDING CHANGES ── -->
    <div *ngIf="hasPendingChanges" class="sp-changes-summary">
      <h3 class="sp-section-title">
        <mat-icon>pending_actions</mat-icon>
        <span i18n="@@Pending Changes">Pending Changes</span>
        <span class="sp-changes-count">{{getPendingChanges().length}}</span>
      </h3>
      <div *ngFor="let change of getPendingChanges()" class="sp-change-item">
        <mat-icon *ngIf="change.type === 'new_order'">add_box</mat-icon>
        <mat-icon *ngIf="change.type === 'add'">arrow_forward</mat-icon>
        <mat-icon *ngIf="change.type === 'remove'">arrow_back</mat-icon>
        <mat-icon *ngIf="change.type === 'change'">swap_horiz</mat-icon>
        <span>{{change.description}}</span>
      </div>
    </div>

    <!-- ── FOOTER ── -->
    <div class="sp-footer">
      <button mat-stroked-button (click)="cancel()" i18n="dialog dismissal|Button that closes a dialog and discards any unsaved edits. It never removes or destroys existing data - the Delete button does that. Must translate to a word meaning 'abandon this action', not 'erase'.@@Cancel">Cancel</button>
      <button mat-raised-button color="primary" (click)="goToReview()"
              [disabled]="!canSave"
              i18n="@@ReviewCommit">Review & Commit</button>
    </div>
  </ng-container>

  <!-- ═══ STAGE 2: REVIEW ═══ -->
  <ng-container *ngIf="stage === 'review'">
    <div class="sp-review">
      <h3 class="sp-section-title">
        <mat-icon>fact_check</mat-icon>
        <span i18n="@@Review Changes">Review Changes</span>
      </h3>

      <div class="sp-review-list">
        <div *ngFor="let change of getPendingChanges()" class="sp-review-item">
          <mat-icon *ngIf="change.type === 'new_order'">add_box</mat-icon>
          <mat-icon *ngIf="change.type === 'add'">arrow_forward</mat-icon>
          <mat-icon *ngIf="change.type === 'remove'">arrow_back</mat-icon>
          <mat-icon *ngIf="change.type === 'change'">swap_horiz</mat-icon>
          <span>{{change.description}}</span>
        </div>
      </div>

      <!-- Inline save error (mirrors ChangePackages style) -->
      <div *ngIf="saveError" class="sp-warning-bar" style="background-color: #fdecea; color: #b71c1c;">
        <mat-icon>error_outline</mat-icon>
        <span>{{saveError}}</span>
      </div>

      <!-- HTTP 409 conflict reasons returned by the server (CLOSED
           ManufacturingSet or printed/SHIPPED Package violations).
           Rendered in red inline so the operator can fix the
           offending row(s) and resubmit. See
           shared/SplitPrintNodeOrder.md. -->
      <div *ngIf="conflictWarnings.length > 0" class="sp-conflict-list sp-warning-bar">
        <mat-icon>block</mat-icon>
        <div>
          <div class="sp-conflict-title" i18n="@@Conflict details">Conflict details:</div>
          <ul>
            <li *ngFor="let c of conflictWarnings">{{c}}</li>
          </ul>
        </div>
      </div>

      <!-- Post-save summary: server-returned warnings[]. Per
           shared/SplitPrintNodeOrder.md, we keep the dialog open so
           the operator can read non-fatal warnings before closing. -->
      <div *ngIf="savedSummaryVisible && saveWarnings.length > 0" class="sp-warnings-list sp-warning-bar">
        <mat-icon>warning_amber</mat-icon>
        <div>
          <div class="sp-warnings-title" i18n="@@Save warnings">Save completed with warnings:</div>
          <ul>
            <li *ngFor="let w of saveWarnings">{{w}}</li>
          </ul>
        </div>
      </div>

      <div class="sp-footer">
        <button *ngIf="!savedSummaryVisible" mat-stroked-button (click)="goBackToEdit()" i18n="@@Back">Back</button>
        <button *ngIf="!savedSummaryVisible" mat-raised-button color="primary" (click)="saveChanges()"
                [disabled]="saving || hasQuantityMismatch" i18n="@@ConfirmSave">
          <mat-spinner *ngIf="saving" diameter="18" class="sp-inline-spinner"></mat-spinner>
          Confirm & Save
        </button>
        <button *ngIf="savedSummaryVisible" mat-raised-button color="primary"
                (click)="closeAfterSummary()" i18n="@@Close">Close</button>
      </div>
    </div>
  </ng-container>
</div>
